import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Receipt, CreditCard, HardHat, Briefcase, PieChart, Settings as SettingsIcon,
  Plus, TrendingUp, TrendingDown, Wallet, AlertTriangle, ClipboardList,
} from "lucide-react";
import {
  fmt, fmt2, pct, useFinance, Stat, Segmented, Field, Modal, Loading, EmptyState, inputCls, kicker, todayISO,
} from "@/admin/finance/financeUtils";
import FinanceExpenses from "@/admin/finance/FinanceExpenses";
import FinancePayments from "@/admin/finance/FinancePayments";
import FinanceWorkers from "@/admin/finance/FinanceWorkers";
import FinanceJobs from "@/admin/finance/FinanceJobs";
import FinanceReports from "@/admin/finance/FinanceReports";
import FinanceSettings from "@/admin/finance/FinanceSettings";

const SUBTABS = [
  ["overview", "Overview", LayoutDashboard],
  ["jobs", "Job Profit", Briefcase],
  ["expenses", "Expenses", Receipt],
  ["payments", "Payments", CreditCard],
  ["workers", "Workers", HardHat],
  ["reports", "Reports", PieChart],
  ["settings", "Settings", SettingsIcon],
];
const PERIODS = [["today", "Today"], ["week", "Week"], ["month", "Month"], ["year", "Year"]].map(([k, label]) => ({ k, label }));

// ---- Quick entry: fast mobile-first forms shared on the overview ----
function QuickExpense({ open, onClose, onSaved, settings }) {
  const [f, setF] = useState({ category: "Fuel", amount: "", vendor: "", payment_method: "Cash", date: todayISO(), notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!Number(f.amount)) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try { await api.post("/finance/expenses", { ...f, amount: Number(f.amount) }); toast.success("Expense added"); onSaved?.(); onClose(); }
    catch (e) { console.error(e); toast.error("Could not save expense"); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Add expense" testid="quick-expense-modal">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount"><input type="number" autoFocus className={inputCls} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="qe-amount" /></Field>
        <Field label="Date"><input type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Category"><select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="qe-category">{(settings?.expense_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
        <Field label="Method"><select className={inputCls} value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })}>{(settings?.payment_methods || []).map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
        <Field label="Vendor"><input className={inputCls} value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} /></Field>
        <Field label="Notes"><input className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      </div>
      <button onClick={save} disabled={busy} data-testid="qe-save" className="mt-4 w-full rounded-xl bg-gold py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">{busy ? "Saving…" : "Save expense"}</button>
    </Modal>
  );
}

export default function AdminFinance() {
  const [sub, setSub] = useState("overview");
  const [period, setPeriod] = useState("month");
  const { data: settings } = useFinance("/finance/settings", []);
  const [quick, setQuick] = useState(null); // 'expense'
  const dash = useFinance(`/finance/dashboard?period=${period}`, [period]);
  const alerts = useFinance("/finance/alerts", []);
  const needs = useFinance("/finance/needs-info", []);

  return (
    <div data-testid="admin-finance">
      {/* VAT verify banner */}
      {settings?.vat_enabled && (
        <div className="mb-4 rounded-xl border border-[color:var(--border-strong)] bg-gold/[0.06] px-4 py-2.5 text-xs text-gold-soft" data-testid="finance-vat-note">
          VAT is ON at {settings.vat_rate}% ({settings.prices_include_vat ? "prices include VAT" : "prices exclude VAT"}). Verify this in Settings.
        </div>
      )}

      {/* Sub navigation */}
      <div className="flex gap-2 mb-6 flex-wrap overflow-x-auto" data-testid="finance-subnav">
        {SUBTABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`finance-tab-${k}`}
            className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm border transition-colors ${sub === k ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {sub === "overview" && (
        <div className="space-y-6" data-testid="finance-overview">
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setQuick("expense")} data-testid="quick-add-expense" className="inline-flex items-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft"><Plus className="h-4 w-4" /> Add expense</button>
            <button onClick={() => setSub("payments")} className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-strong)] px-4 py-2.5 text-sm text-white hover:bg-white/5"><CreditCard className="h-4 w-4 text-gold-soft" /> Record payment</button>
            <button onClick={() => setSub("workers")} className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-strong)] px-4 py-2.5 text-sm text-white hover:bg-white/5"><HardHat className="h-4 w-4 text-gold-soft" /> Add worker hours</button>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className={kicker}>Business overview</p>
            <Segmented options={PERIODS} value={period} onChange={setPeriod} testid="finance-period" />
          </div>

          {dash.loading && <Loading />}
          {dash.data && !dash.data.has_data && <EmptyState label="No finance data yet — add an expense, record a payment, or log worker hours to get started." />}
          {dash.data && dash.data.has_data && (() => {
            const d = dash.data;
            return (
              <>
                {/* Money In / Out / Profit — the simple headline */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="ascendia-card p-5" data-testid="finance-money-in"><div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-400" /><p className="text-[11px] uppercase tracking-wide text-muted-c">Money in (collected)</p></div><p className="mt-2 font-display text-3xl font-semibold text-white tabular-nums">{fmt(d.collected)}</p><p className="mt-1 text-xs text-muted-c">Revenue booked {fmt(d.revenue.window)}</p></div>
                  <div className="ascendia-card p-5" data-testid="finance-money-out"><div className="flex items-center gap-2"><TrendingDown className="h-4 w-4 text-rose-400" /><p className="text-[11px] uppercase tracking-wide text-muted-c">Money out (expenses)</p></div><p className="mt-2 font-display text-3xl font-semibold text-white tabular-nums">{fmt(d.expenses.total)}</p><p className="mt-1 text-xs text-muted-c">Labor {fmt(d.expenses.labor)} · Supplies {fmt(d.expenses.supplies)}</p></div>
                  <div className="ascendia-card p-5" data-testid="finance-net-profit"><div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-gold-soft" /><p className="text-[11px] uppercase tracking-wide text-muted-c">Net profit</p></div><p className={`mt-2 font-display text-3xl font-semibold tabular-nums ${d.profit.net < 0 ? "text-rose-300" : "text-white"}`}>{fmt(d.profit.net)}</p><p className="mt-1 text-xs text-muted-c">Margin {pct(d.profit.margin)}</p></div>
                </div>

                {/* Revenue by period */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Stat label="Revenue today" value={fmt(d.revenue.today)} testid="rev-today" />
                  <Stat label="This week" value={fmt(d.revenue.week)} />
                  <Stat label="This month" value={fmt(d.revenue.month)} />
                  <Stat label="This year" value={fmt(d.revenue.year)} />
                </div>

                {/* Secondary KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Stat label="Outstanding" value={fmt(d.outstanding)} tone={d.outstanding > 0 ? "text-amber-300" : "text-white"} testid="kpi-outstanding" />
                  <Stat label="Avg job value" value={fmt(d.kpi.avg_job_value)} />
                  <Stat label="Avg profit / job" value={fmt(d.kpi.avg_profit_per_job)} />
                  <Stat label="Revenue / labor hr" value={fmt(d.kpi.revenue_per_labor_hour)} />
                  <Stat label="Gross profit" value={fmt(d.profit.gross)} />
                  <Stat label="Refunds" value={fmt(d.expenses.refunds)} />
                  <Stat label="Discounts" value={fmt(d.expenses.discounts)} />
                  <Stat label="Jobs in period" value={d.kpi.jobs} />
                </div>

                {/* VAT */}
                {d.vat.enabled && (
                  <div className="ascendia-card p-5" data-testid="finance-vat">
                    <p className={kicker}>VAT ({d.vat.rate}%)</p>
                    <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                      <div><p className="text-muted-c text-xs">Revenue before VAT</p><p className="text-white tabular-nums">{fmt2(d.vat.revenue_before_vat)}</p></div>
                      <div><p className="text-muted-c text-xs">VAT collected</p><p className="text-gold-soft tabular-nums">{fmt2(d.vat.vat_collected)}</p></div>
                      <div><p className="text-muted-c text-xs">Revenue (incl VAT)</p><p className="text-white tabular-nums">{fmt2(d.vat.revenue_after_vat)}</p></div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Alerts + needs info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="ascendia-card p-5" data-testid="finance-alerts">
              <p className={kicker}><AlertTriangle className="inline h-3.5 w-3.5 mr-1 text-amber-300" /> Alerts</p>
              <div className="mt-3 space-y-2">
                {(alerts.data || []).slice(0, 6).map((a, i) => (
                  <p key={i} className={`text-xs ${a.level === "error" ? "text-rose-300" : "text-amber-200"}`}>⚠️ {a.msg}</p>
                ))}
                {alerts.data && alerts.data.length === 0 && <p className="text-xs text-muted-c">No alerts — all good.</p>}
              </div>
            </div>
            <div className="ascendia-card p-5" data-testid="finance-needs-info">
              <div className="flex items-center justify-between"><p className={kicker}><ClipboardList className="inline h-3.5 w-3.5 mr-1 text-gold-soft" /> Needs financial info</p>{needs.data?.length > 0 && <span className="text-xs text-gold-soft">{needs.data.length}</span>}</div>
              <div className="mt-3 space-y-2">
                {(needs.data || []).slice(0, 6).map((n) => (
                  <button key={n.job_id} onClick={() => setSub("jobs")} className="block w-full text-left text-xs text-secondary hover:text-white">{n.ref} · {n.client_name} — <span className="text-muted-c">{n.issues.join(", ")}</span></button>
                ))}
                {needs.data && needs.data.length === 0 && <p className="text-xs text-muted-c">Nothing missing — every job has its facts.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {sub === "jobs" && <FinanceJobs settings={settings} />}
      {sub === "expenses" && <FinanceExpenses settings={settings} />}
      {sub === "payments" && <FinancePayments settings={settings} />}
      {sub === "workers" && <FinanceWorkers settings={settings} />}
      {sub === "reports" && <FinanceReports settings={settings} />}
      {sub === "settings" && <FinanceSettings />}

      <QuickExpense open={quick === "expense"} onClose={() => setQuick(null)} onSaved={() => { dash.reload(); }} settings={settings} />
    </div>
  );
}
